import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAccount } from '../../contexts/Account';
import { searchCompanies } from '../../services/advisor_services';
import BoardCard from '../BoardCard';
import BoardSkeleton from '../../components/SkeletonLoading/boardSkeleton';
import eventBus, { eventBusValues } from '../../eventBus';

const sanitizeLogo = (company = {}) => {
  if (company.companyLogo) return company.companyLogo;
  if (company.image?.location) {
    return company.image.location.replace(/^https?:\/\//, '');
  }
  return null;
};

const getRandomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];

const PERK_CATEGORY_MAP = {
  'Equity Potential': ['Equity Potential', '1% Equity Pledge'],
  'Advisor Rate Opportunity': [
    'Advisor Rate Opportunity',
    '$1k Golden Idea Bonus',
  ],
  'Fractional or Full-Time Roles': ['Formal Roles'],
  'Product or Service Discounts': [
    'Board Member Discount',
    'Complimentary Product',
  ],
};

const sanitizePerkLabel = (value = '') =>
  value
    .replace(/^[^\w\s]+ /, '')
    .split(' - ')[0]
    .trim();

const resolvePerkCategories = (value = '') => {
  const sanitized = sanitizePerkLabel(value);
  if (!sanitized) return [];
  if (!Object.prototype.hasOwnProperty.call(PERK_CATEGORY_MAP, sanitized)) {
    return [];
  }
  const mapped = PERK_CATEGORY_MAP[sanitized];
  if (!mapped) return [];
  if (Array.isArray(mapped)) {
    return mapped.filter(Boolean);
  }
  return mapped ? [mapped] : [];
};

const PerkBoards = () => {
  const { accountDetails } = useAccount();
  const [perk, setPerk] = useState('');
  const [companies, setCompanies] = useState([]);
  const [hideScroll, setHideScroll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const shouldMeasure = isLoading || companies.length > 0;

  useEffect(() => {
    if (!shouldMeasure) return undefined;

    const element = containerRef.current;
    if (!element) return undefined;

    const compute = () => {
      const rect = element.getBoundingClientRect();
      const measured = rect?.width || element.offsetWidth || 0;
      const viewportWidth = Math.max(0, (window.innerWidth || 0) - 80);
      const next =
        measured > 0 ? Math.min(measured, viewportWidth) : viewportWidth;
      setWidth(next);
    };

    // Measure after paint to avoid 0/null
    requestAnimationFrame(compute);
    window.addEventListener('resize', compute);

    const observer = new ResizeObserver(() => requestAnimationFrame(compute));
    observer.observe(element);

    return () => {
      window.removeEventListener('resize', compute);
      observer.disconnect();
    };
  }, [shouldMeasure]);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollWidth, clientWidth } = container;
    const compareWidth = width || clientWidth || 0;
    if (scrollWidth <= compareWidth) setHideScroll(true);
    else setHideScroll(false);
  }, [width, companies, isXs, isLoading]);

  useEffect(() => {
    // console.log(
    //   'Account preferred perks:',
    //   accountDetails?.boardMatchPreferredPerks
    // );
    const preferred = Array.isArray(accountDetails?.boardMatchPreferredPerks)
      ? accountDetails.boardMatchPreferredPerks
          .map(sanitizePerkLabel)
          .filter((item) => resolvePerkCategories(item).length > 0)
      : [];
    const available = Object.keys(PERK_CATEGORY_MAP).filter(
      (item) => resolvePerkCategories(item).length > 0
    );
    if (preferred.length > 0) {
      const selected = getRandomItem(preferred);
      setPerk(selected);
      return;
    }
    const fallback = getRandomItem(available);
    if (fallback) setPerk(fallback);
  }, [accountDetails?.boardMatchPreferredPerks]);

  const searchPerks = useMemo(() => resolvePerkCategories(perk), [perk]);
  const shouldFilterByPerk = searchPerks.length > 0;

  useEffect(() => {
    if (!accountDetails?.id || !shouldFilterByPerk) {
      setCompanies([]);
      setIsLoading(false);
      return;
    }
    let isCurrent = true;
    setIsLoading(true);
    Promise.all(
      searchPerks.map((perkCategory) =>
        searchCompanies({
          ADVISOR_ID: accountDetails.id,
          PEER_FILTER: 'Company',
          PERK: perkCategory,
          OFFSET: 0,
        })
          .then((response) => response?.data?.searchCompanies || [])
          .catch((error) => {
            console.error(
              `Error searching companies by perk "${perkCategory}":`,
              error
            );
            return [];
          })
      )
    )
      .then((responses) => {
        if (!isCurrent) return;
        const list = responses.flat();
        const filtered = list
          .filter(
            (c) =>
              c.boardOwnerStatus !== 'inactive' &&
              c.boardOwnerStatus !== 'paused' &&
              !['dormant', 'pending_cancellation', 'position_filled'].includes(
                c.opportunityStage
              )
          )
          .filter((c) =>
            !shouldFilterByPerk
              ? true
              : Array.isArray(c.benefits)
              ? c.benefits.some(
                  (b) => searchPerks.includes(b.category) && b.featured
                )
              : false
          )
          .map((c) => ({ ...c, companyLogo: sanitizeLogo(c) }));
        const deduped = Array.from(
          new Map(filtered.map((c) => [c.id, c])).values()
        );
        const sorted = deduped.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setCompanies(sorted);
      })
      .catch((e) => {
        if (!isCurrent) return;
        console.error('Error searching companies by perk:', e);
        setCompanies([]);
      })
      .finally(() => {
        if (!isCurrent) return;
        setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [accountDetails?.id, shouldFilterByPerk, searchPerks]);

  const total = companies.length;
  let displayed = companies;
  // if (isXs && !expanded && total > 2) displayed = companies.slice(0, 2);

  if (!isLoading && total === 0) return null;

  const showMoreButton = isXs && total > 2;

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const jump = 300;
    if (scrollLeft + clientWidth + 50 >= scrollWidth) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: jump, behavior: 'smooth' });
    }
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const jump = 300;
    if (container.scrollLeft === 0) {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -jump, behavior: 'smooth' });
    }
  };

  const handleCardClick = (company) => {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
      companyId: company.id,
    });
  };

  return (
    <Paper
      id="perk-boards-tile"
      ref={containerRef}
      elevation={1}
      sx={{
        padding: '15px',
        paddingRight: { xxs: '26px', xs: '15px' },
        borderRadius: { xxs: '0', xs: '12px' },
        marginBottom: '20px',
        boxShadow: 'none',
        width: { xxs: '110%', xs: '100%' },
        marginLeft: { xxs: '-10px', xs: '0px' },
        position: 'relative',
        border: '1px solid #E0E0E0',
        display: isLoading || total > 0 ? 'block' : 'none',
      }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: '20px', mb: '4px', color: '#001030' }}
      >
        {`📈 Boards Offering ${perk}`}
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 14,
          color: '#4D586E',
          fontStyle: 'italic',
          mb: '16px',
        }}
      >
        Join one of these boards to start enjoying exciting perks & rewards
        being offered for motivated board members
      </Typography>

      {/* {!hideScroll && displayed.length > 1 && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            left: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )} */}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'visible',
          whiteSpace: 'nowrap',
          scrollBehavior: 'smooth',
          overscrollBehaviorX: 'contain',
          marginRight: { xxs: 0, xs: hideScroll ? 0 : '40px' },
          maxWidth: {
            xxs: '100%',
            xs: width - 40,
            md: width + 200,
            lg: width + 500,
          },
        }}
      >
        {isLoading
          ? Array.from({ length: isXs ? 1 : 3 }).map((_, index) => (
              <Box
                key={`perk-skeleton-${index}`}
                sx={{
                  marginBottom: '20px',
                  paddingTop: '10px',
                  marginRight: '16px',
                  width: { xxs: '280px', xs: '350px' },
                }}
              >
                <BoardSkeleton />
              </Box>
            ))
          : displayed.map((company) => (
              <Box
                key={`perk-${company.id}`}
                sx={{
                  marginBottom: '20px',
                  paddingTop: '10px',
                  marginRight: '16px',
                  width: { xxs: '280px', xs: '350px' },
                }}
              >
                <BoardCard
                  board={company}
                  hideMemberSince
                  companyList
                  width={isXs ? '280px' : '350px'}
                  onClick={() => handleCardClick(company)}
                  displayPillsFlag
                />
              </Box>
            ))}
      </Box>

      {!hideScroll && displayed.length > 1 && (
        <IconButton
          onClick={scrollRight}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            right: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* {showMoreButton && (
        <Box sx={{ textAlign: 'center', mt: '0px' }}>
          <Button
            variant="text"
            onClick={() => setExpanded(!expanded)}
            sx={{
              border: '1px solid #2C71F64D',
              borderRadius: '8px',
              color: '#2C71F6',
              backgroundColor: '#fff',
              p: '4px 16px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              width: '100%',
              height: '40px',
              '&:hover': { backgroundColor: '#F3F4F6' },
            }}
          >
            {expanded ? 'Less' : 'More'}
          </Button>
        </Box>
      )} */}
    </Paper>
  );
};

export default PerkBoards;
